import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { stockService } from '../services/api';
import { DotLoader } from 'react-spinners';
import './DistanceToMA.css';

const DistanceToMA = ({ sectors }) => {
  const [stockData, setStockData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const abortControllerRef = useRef(null);

  // Fetch MA data on mount (sectors come from Dashboard props)
  const fetchMAData = useCallback(async (signal) => {
    setLoading(true);

    try {
      const maResponse = await stockService.getMAOverview(signal);

      if (maResponse && maResponse.stocks) {
        const dataMap = {};
        maResponse.stocks.forEach(stock => {
          dataMap[stock.symbol] = stock;
        });
        setStockData(dataMap);
      }

      setError(null);
      setLoading(false);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Error fetching MA data:', err);
        setError('Không thể tải dữ liệu');
      }
      setLoading(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    fetchMAData(abortControllerRef.current.signal);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchMAData]);

  useEffect(() => {
    const isLoadingState = loading || sectors.length === 0;
    if (isLoadingState) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }
    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading, sectors.length]);

  const getDistanceClass = (distance) => {
    if (distance === null || distance === undefined) return 'ma-neutral';
    if (distance > 5) return 'ma-strong-positive';
    if (distance > 0) return 'ma-positive';
    if (distance > -5) return 'ma-negative';
    return 'ma-strong-negative';
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return price;
  };

  const formatDistance = (distance) => {
    if (distance === null || distance === undefined) return '-';
    const prefix = distance > 0 ? '+' : '';
    return `${prefix}${distance.toFixed(1)}`;
  };

  // Compute sector breadth stats: % of stocks above each MA per sector + total
  const breadthStats = useMemo(() => {
    if (!sectors.length || !Object.keys(stockData).length) return null;

    const maKeys = ['ma20_distance', 'ma50_distance', 'ma90_distance', 'ma200_distance'];
    const sectorRows = [];
    const totals = { ma20: { above: 0, total: 0 }, ma50: { above: 0, total: 0 }, ma90: { above: 0, total: 0 }, ma200: { above: 0, total: 0 } };

    sectors.forEach(sector => {
      const counts = {};
      maKeys.forEach(k => { counts[k] = { above: 0, total: 0 }; });

      sector.symbols.forEach(sym => {
        const stock = stockData[sym];
        if (!stock) return;
        maKeys.forEach(k => {
          if (stock[k] != null) {
            counts[k].total++;
            if (stock[k] > 0) counts[k].above++;
          }
        });
      });

      const row = { name: sector.name };
      maKeys.forEach((k, i) => {
        const shortKey = ['ma20', 'ma50', 'ma90', 'ma200'][i];
        const c = counts[k];
        row[shortKey] = c.total > 0 ? { above: c.above, total: c.total, pct: Math.round((c.above / c.total) * 100) } : null;
        totals[shortKey].above += c.above;
        totals[shortKey].total += c.total;
      });
      sectorRows.push(row);
    });

    const totalRow = { name: 'Tổng' };
    ['ma20', 'ma50', 'ma90', 'ma200'].forEach(k => {
      const t = totals[k];
      totalRow[k] = t.total > 0 ? { above: t.above, total: t.total, pct: Math.round((t.above / t.total) * 100) } : null;
    });

    return { sectors: sectorRows, total: totalRow };
  }, [sectors, stockData]);

  // Show loading if still fetching or if sectors haven't loaded yet
  const isLoading = loading || sectors.length === 0;

  return (
    <div className="ma-container">
      {isLoading && (
        <div className="ma-loading-overlay">
          <DotLoader color="#3b82f6" size={50} />
        </div>
      )}

      {/* Toast Notification */}
      {error && (
        <div className="toast-container">
          <div className="toast error">
            <span className="toast-message">{error}</span>
            <button className="toast-close" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      {/* Fixed Header Section */}
      <div className="ma-fixed-header">
        <div className="ma-header">
          <span className="header-title">Đường Trung bình</span>
        </div>
      </div>

      {/* Scrollable Sectors Grid */}
      <div className="ma-scroll-container">
        <div className="ma-sectors-grid">
          {sectors.map((sector) => (
            <div key={sector.id} className="ma-sector-card">
              <div className="ma-sector-header">
                <h3>{sector.name}</h3>
              </div>
              <div className="ma-sector-table">
                <div className="ma-table-header">
                  <span className="ma-col-symbol">MÃ</span>
                  <span className="ma-col-price">GIÁ</span>
                  <span className="ma-col-dist">MA20</span>
                  <span className="ma-col-dist">MA50</span>
                  <span className="ma-col-dist">MA90</span>
                  <span className="ma-col-dist">MA200</span>
                </div>
                <div className="ma-table-body">
                  {[...sector.symbols]
                    .sort((a, b) => {
                      const distA = stockData[a]?.ma90_distance || 0;
                      const distB = stockData[b]?.ma90_distance || 0;
                      return distB - distA;
                    })
                    .map((symbol) => {
                    const stock = stockData[symbol];

                    return (
                      <div key={symbol} className="ma-table-row">
                        <span className="ma-col-symbol">{symbol}</span>
                        <span className="ma-col-price">{formatPrice(stock?.price)}</span>
                        <span className={`ma-col-dist ${getDistanceClass(stock?.ma20_distance)}`}>
                          {formatDistance(stock?.ma20_distance)}
                        </span>
                        <span className={`ma-col-dist ${getDistanceClass(stock?.ma50_distance)}`}>
                          {formatDistance(stock?.ma50_distance)}
                        </span>
                        <span className={`ma-col-dist ${getDistanceClass(stock?.ma90_distance)}`}>
                          {formatDistance(stock?.ma90_distance)}
                        </span>
                        <span className={`ma-col-dist ${getDistanceClass(stock?.ma200_distance)}`}>
                          {formatDistance(stock?.ma200_distance)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAB Stats Button */}
      <button className="fab-stats-btn" onClick={() => setShowStats(true)}>
        <span className="fab-tooltip">Thống kê</span>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"></line>
          <line x1="12" y1="20" x2="12" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="14"></line>
        </svg>
      </button>

      {/* Stats Modal */}
      {showStats && breadthStats && (
        <div className="stats-overlay" onClick={() => setShowStats(false)}>
          <div className="stats-modal" onClick={e => e.stopPropagation()}>
            <div className="stats-modal-header">
              <h3>Thống kê % mã trên MA theo ngành</h3>
              <button className="stats-close-btn" onClick={() => setShowStats(false)}>×</button>
            </div>
            <div className="stats-modal-body">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th className="stats-col-name">Ngành</th>
                    <th>MA20</th>
                    <th>MA50</th>
                    <th>MA90</th>
                    <th>MA200</th>
                  </tr>
                </thead>
                <tbody>
                  {breadthStats.sectors.map((row, i) => (
                    <tr key={i}>
                      <td className="stats-col-name">{row.name}</td>
                      {['ma20', 'ma50', 'ma90', 'ma200'].map(k => (
                        <td key={k} className={row[k] ? `stats-pct ${row[k].pct >= 50 ? 'pct-high' : row[k].pct >= 25 ? 'pct-mid' : 'pct-low'}` : 'stats-pct'}>
                          {row[k] ? `${row[k].pct}%` : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="stats-total-row">
                    <td className="stats-col-name">Tổng</td>
                    {['ma20', 'ma50', 'ma90', 'ma200'].map(k => {
                      const t = breadthStats.total[k];
                      return (
                        <td key={k} className={t ? `stats-pct ${t.pct >= 50 ? 'pct-high' : t.pct >= 25 ? 'pct-mid' : 'pct-low'}` : 'stats-pct'}>
                          {t ? `${t.pct}%` : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistanceToMA;
